
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Careers() {
  useEffect(() => {
    // Update page title and meta
    document.title = "川崎市訪問介護スタッフ採用情報 - 未経験歓迎 | ゆきのケア";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '川崎市の訪問介護ゆきのケアでスタッフ募集中。未経験・ブランクOK、週1日から勤務可能。身体介護時給1,800円〜、生活援助時給1,500円〜。川崎市幸区・川崎区・横浜市鶴見区。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '訪問介護,求人,採用,川崎市,未経験歓迎,パート,アルバイト,ヘルパー');
    }

    // Schema.org JSON-LD for JobPosting
    const jobPostingSchema = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "訪問介護スタッフ（登録ヘルパー）",
      "description": "川崎市の訪問介護ゆきのケアでスタッフ募集。未経験・ブランクOK、週1日から勤務可能。",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "株式会社歩",
        "sameAs": `${import.meta.env.VITE_SITE_URL}/`
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "戸手本町1-123-12 ドルフ5 203",
          "addressLocality": "川崎市幸区",
          "addressRegion": "神奈川県",
          "postalCode": "212-0023",
          "addressCountry": "JP"
        }
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "JPY",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": 1500,
          "maxValue": 1800,
          "unitText": "HOUR"
        }
      },
      "employmentType": "PART_TIME",
      "qualifications": "介護職員初任者研修（旧ホームヘルパー2級）以上",
      "workHours": "週1日・1時間からOK",
      "benefits": "介護福祉士手当・土日祝手当あり",
      "datePosted": new Date().toISOString().split('T')[0]
    };

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "川崎市訪問介護スタッフ採用情報 - 未経験歓迎",
      "description": "川崎市の訪問介護ゆきのケアでスタッフ募集中。未経験・ブランクOK、週1日から勤務可能。",
      "url": `${import.meta.env.VITE_SITE_URL}/careers`,
      "mainEntity": jobPostingSchema
    };

    // Add schema to head
    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.textContent = JSON.stringify(jobPostingSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(script2);

    return () => {
      // Cleanup
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/4e28e17ba4e737ad2b4d0213d4252ea8/96aaf8d56f57e04898441455e82ac9b1.png" 
                alt="訪問介護ゆきのケア" 
                title="訪問介護ゆきのケア 川崎市"
                className="h-16 w-auto object-contain"
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium">ホーム</Link>
              <Link to="/services" className="text-gray-700 hover:text-blue-600 font-medium">サービス内容</Link>
              <Link to="/careers" className="text-blue-600 font-medium">採用情報</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://public.readdy.ai/ai/img_res/a9ba3f9a19ee7cc722e567abc480ff8a.jpg')`
        }}
      >
        <div className="text-center text-white px-4 max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            一緒に"やさしさ"を届けませんか？
          </h2>
          <p className="text-xl md:text-2xl font-medium">
            あなたの笑顔が、地域の安心につながります。
          </p>
        </div>
      </section>

      {/* メッセージ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-8" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            地域の方々を支える仲間を募集しています
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-8 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                ゆきのケアでは、ご利用者さま一人ひとりに寄り添う温かいケアを目指しています。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                未経験者・ブランクのある方も歓迎。家庭や生活に合わせて働ける環境を整えています。
              </p>
            </div>
            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20home%20care%20team%20meeting%20in%20comfortable%20office%20setting%2C%20diverse%20group%20of%20Japanese%20caregivers%20in%20casual%20work%20clothes%20discussing%20care%20plans%20around%20table%2C%20warm%20Japanese%20office%20environment%20with%20natural%20lighting%2C%20collaborative%20atmosphere%20in%20authentic%20Japanese%20workplace%20showing%20genuine%20teamwork%2C%20realistic%20photography%20with%20natural%20expressions&width=800&height=400&seq=team4&orientation=landscape" 
                alt="チームワーク" 
                className="w-full h-64 object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 募集要項 */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            募集要項
          </h2>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 bg-blue-50 font-semibold text-gray-800 w-1/4">募集職種</td>
                    <td className="px-6 py-4 text-gray-700">訪問介護スタッフ（登録ヘルパー）</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 bg-blue-50 font-semibold text-gray-800">雇用形態</td>
                    <td className="px-6 py-4 text-gray-700">パート・アルバイト（登録制）</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 bg-blue-50 font-semibold text-gray-800">資格</td>
                    <td className="px-6 py-4 text-gray-700">介護職員初任者研修（旧ホームヘルパー2級）以上</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 bg-blue-50 font-semibold text-gray-800">給与</td>
                    <td className="px-6 py-4 text-gray-700">
                      <div className="space-y-1">
                        <p>身体介護：時給1,800円〜</p>
                        <p>生活援助：時給1,500円〜</p>
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 bg-blue-50 font-semibold text-gray-800">手当</td>
                    <td className="px-6 py-4 text-gray-700">介護福祉士手当・土日祝手当あり</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 bg-blue-50 font-semibold text-gray-800">勤務時間</td>
                    <td className="px-6 py-4 text-gray-700">週1日・1時間からOK／シフト自由</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 bg-blue-50 font-semibold text-gray-800">勤務地</td>
                    <td className="px-6 py-4 text-gray-700">神奈川県川崎市幸区戸手本町1-123-12 ドルフ5 203</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 bg-blue-50 font-semibold text-gray-800">備考</td>
                    <td className="px-6 py-4 text-gray-700">年齢・性別不問／WワークOK／ブランク歓迎</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* 働きやすさのポイント */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            働きやすさのポイント
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-orange-50 rounded-xl">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">柔軟なシフト</h3>
              <p className="text-gray-600 text-sm">
                家庭と両立可能な働き方をサポートします
              </p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-heart-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">サポート体制万全</h3>
              <p className="text-gray-600 text-sm">
                未経験者歓迎・丁寧な研修制度があります
              </p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-xl">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">良好なコミュニケーション</h3>
              <p className="text-gray-600 text-sm">
                スタッフ間の連携を大切にしています
              </p>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-xl">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-graduation-cap-line text-3xl text-pink-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">資格取得支援</h3>
              <p className="text-gray-600 text-sm">
                スキルアップを応援する制度があります
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 先輩スタッフの声 */}
      <section className="py-20 bg-gradient-to-r from-orange-50 to-pink-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            先輩スタッフの声
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-smile-line text-3xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">田中さん（30代・女性）</h3>
                  <p className="text-gray-600">勤務歴2年</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                「子育てと両立しながら働けるのが魅力です。シフトの融通が利くので、子どもの行事にも参加できます。ご利用者さまからの『ありがとう』の言葉が何よりのやりがいです。」
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-smile-line text-3xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">佐藤さん（50代・女性）</h3>
                  <p className="text-gray-600">勤務歴3年</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                「介護の仕事は初めてでしたが、丁寧な研修とサポートのおかげで安心して働けています。スタッフ同士の仲が良く、困った時はすぐに相談できる環境です。」
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 応募・お問い合わせ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-8" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            応募・お問い合わせ
          </h2>
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              採用に関するご質問やご応募は外部フォームより受け付けています。
            </p>
            <p className="text-gray-600 mb-8">
              まずはお気軽にお問い合わせください。面談日程などご相談に応じます。
            </p>
            <button className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-mail-line mr-2"></i>
              応募・お問い合わせはこちら
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-8 text-left">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                <i className="ri-phone-line mr-2 text-blue-600"></i>
                お電話でのお問い合わせ
              </h3>
              <p className="text-2xl font-bold text-blue-600 mb-2">044-280-7266</p>
              <p className="text-gray-600">受付時間：平日 9:00〜17:00</p>
            </div>
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                <i className="ri-map-pin-line mr-2 text-green-600"></i>
                事業所所在地
              </h3>
              <p className="text-gray-700">
                〒212-0023<br />
                神奈川県川崎市幸区戸手本町1-123-12<br />
                ドルフ5 203
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">株式会社歩</h3>
              <div className="space-y-2 text-gray-300">
                <p>〒212-0032 神奈川県川崎市幸区戸手本町1-123-12 ドルフ5 203</p>
                <p>Tel：044-280-7266</p>
                <p>Fax：044-280-7267</p>
                <p>介護保険事業所番号：1475102685</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">会社情報</h3>
              <div className="space-y-2 text-gray-300">
                <p>設立：平成29年2月1日</p>
                <p>資本金：200万円</p>
                <p>代表取締役：吉田 香</p>
                <p>管理者：菊池 雪野</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">© 2025 訪問介護ゆきのケア All Rights Reserved.</p>
            <a href="https://readdy.ai/?origin=logo" className="text-blue-400 hover:text-blue-300 ml-4">Website Builder</a>
          </div>
        </div>
      </footer>

      {/* 固定お問い合わせボタン */}
      <div className="fixed bottom-6 right-6 z-50">
        <a 
          href="https://ws.formzu.net/dist/S634209674/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full shadow-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center"
        >
          <i className="ri-phone-line mr-2"></i>
          応募・お問い合わせはこちら
        </a>
      </div>
    </div>
  );
}
